import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';
import { useIntl } from 'react-intl';
import { isEmpty, get } from 'lodash';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Redirect, useHistory } from 'react-router-dom';

import TextField from '../../../components/Inputs/TextField';
import PasswordField from '../../../components/Inputs/PasswordField';
import Box from '../../../components/Styled/Box';
import Button from '../../../components/Buttons/Button';
import CpfField from '../../../components/Inputs/CpfField';
import TermsOfUse from '../../../components/Inputs/TermsOfUse';
import Recaptcha from '../../../components/Recaptcha';

import { useIsMobileLayout, useRoutes } from '../../../hooks';
import { joinUrlWithParams } from '../../../utils';

import { authActions, applicationActions } from '../../../redux/actions';
import { authSelectors } from '../../../redux/selectors';

import { areValidAuthorizationHeaders, isValidCpf } from '../../../utils/validators';

import { statusByToken } from '../../../services/apis/respondents/applications-questionnaires.api';

const validationSchema = (f, questionnaireFields) => Yup.object().shape({
  first_name: Yup.lazy(() => {
    if (get(questionnaireFields, 'first_name.required')) {
      return Yup.string().trim().required(f({ id: 'errors.required.simple' }));
    }
    return Yup.string();
  }),
  last_name: Yup.lazy(() => {
    if (get(questionnaireFields, 'last_name.required')) {
      return Yup.string().trim().required(f({ id: 'errors.required.simple' }));
    }
    return Yup.string();
  }),
  cpf: Yup.lazy(() => {
    if (get(questionnaireFields, 'cpf.required')) {
      return Yup.string()
        .test('cpf', f({ id: 'errors.invalid.cpf' }), isValidCpf)
        .required(f({ id: 'errors.required.simple' }));
    }
    return Yup.string().test('cpf', f({ id: 'errors.invalid.cpf' }), isValidCpf);
  }),
  password: Yup.string()
    .min(6, f({ id: 'errors.min_string_len' }, { num: 6 }))
    .test('maiusculo', f({ id: 'errors.min_uppercase_letters' }, { num: 1 }), (val) => (val || '').toLowerCase() !== val)
    .test('minusculo', f({ id: 'errors.min_lowercase_letters' }, { num: 1 }), (val) => (val || '').toUpperCase() !== val)
    .required(f({ id: 'errors.required.simple' })),
  password_confirmation: Yup.string()
    .oneOf([Yup.ref('password'), null], f({ id: 'errors.passwords_do_not_match' }))
    .required(f({ id: 'errors.required.simple' })),
  not_a_robot: Yup.bool().oneOf([true]),
  legal_agreement: Yup.bool().oneOf([true]),
});

const initialValues = {
  first_name: '',
  last_name: '',
  cpf: '',
  password: '',
  password_confirmation: '',
  not_a_robot: false,
  legal_agreement: false,
};

const SignUpFromEmailForm = ({ location }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { formatMessage: f } = useIntl();
  const headers = React.useMemo(() => queryString.parse(location.search), [location]);
  const routes = useRoutes();
  const isMobile = useIsMobileLayout();
  const isAuthenticated = useSelector(authSelectors.isAuthenticated);

  const [questionnaireFields, setQuestionnaireFields] = useState();

  const onSubmit = React.useCallback((values) => {
    dispatch(authActions.updateUnauthenticatedUserRequest({
      user: values,
      headers,
    }));
  }, [dispatch, headers]);

  const formik = useFormik({
    initialValues,
    onSubmit,
    validateOnMount: true,
    validationSchema: validationSchema(f),
  });

  const fetchQuestionnaireStatus = useCallback(async () => {
    const { application_questionnaire_status: status, ...data } = await statusByToken({
      applicationQuestionnaireToken: headers.aq_tkn,
      link_type: 'authenticated',
      apiArgs: {
        options: { toggleLoading: false },
      },
    });

    const notYetReleased = ['no_date_yet', 'created', 'paused'].includes(status);
    const isExpiredQuestionnaire = ['ended', 'not_answered', 'results_available'].includes(status);

    if (notYetReleased) {
      history.push(routes.messages.notStarted);
    }

    else if (isExpiredQuestionnaire) {
      history.push(routes.messages.expired);
    }

    else {
      setQuestionnaireFields(data);
    }
  }, [headers.aq_tkn, history, routes.messages]);

  useEffect(() => {
    // Limpa URL. Header já está armazenado.
    if (!isEmpty(headers) && areValidAuthorizationHeaders(headers)) {
      window.history.pushState('', '', '/finish-signup');
    }
  }, [headers]);

  useEffect(() => {
    if (!isEmpty(headers) && areValidAuthorizationHeaders(headers)) {
      dispatch(applicationActions.toggleDialog({
        title: f({ id: 'forms.signup.dialog_warning.title' }),
        body: f({ id: 'forms.signup.dialog_warning.message' }, { email: headers.uid }),
        leftButton: {
          text: f({ id: 'forms.signup.dialog_warning.left_button' }),
          onClick: () => history.push(routes.messages.invalidLink),
        },
        button: {
          text: f({ id: 'forms.signup.dialog_warning.right_button' }),
        },
      }));
    }
  }, [f, dispatch, routes, history, headers]);

  useEffect(() => {
    if (headers.aq_tkn) {
      fetchQuestionnaireStatus();
    }
  }, [headers, fetchQuestionnaireStatus]);

  // redireciona usuario se ele estiver logado
  if (isAuthenticated) {
    const params = [
      headers.aq_tkn && `aq_tkn=${headers.aq_tkn}`,
    ].filter((d) => d).join('&');

    return <Redirect to={joinUrlWithParams(routes.home, params)} />;
  }

  // usuário não tem nada a fazer aqui se ele n tiver esses headers
  if (isEmpty(headers) || !areValidAuthorizationHeaders(headers)) {
    const params = [
      headers.aq_tkn && `aq_tkn=${headers.aq_tkn}`,
    ].filter((d) => d).join('&');

    return <Redirect to={joinUrlWithParams(routes.login, params)} />;
  }

  if (!headers.aq_tkn) {
    return <Redirect to={routes.login} />;
  }

  const disabledButton = (
    !formik.isValid
    || formik.isSubmitting
  );

  return (
    <form onSubmit={formik.handleSubmit}>
      {get(questionnaireFields, 'first_name.visible') && (
        <TextField
          required={get(questionnaireFields, 'first_name.required')}
          boxProps={{ mt: 3 }}
          name='first_name'
          type='text'
          value={formik.values.first_name}
          onChange={formik.handleChange('first_name')}
          onBlur={formik.handleBlur('first_name')}
          label={f({ id: 'forms.signup.first_name_label' })}
          placeholder={f({ id: 'forms.signup.first_name_placeholder' })}
          error={formik.touched.first_name ? formik.errors.first_name : ''}
        />
      )}

      {get(questionnaireFields, 'last_name.visible') && (
        <TextField
          required={get(questionnaireFields, 'last_name.required')}
          boxProps={{ mt: 3 }}
          name='last_name'
          type='text'
          value={formik.values.last_name}
          onChange={formik.handleChange('last_name')}
          onBlur={formik.handleBlur('last_name')}
          label={f({ id: 'forms.signup.last_name_label' })}
          placeholder={f({ id: 'forms.signup.last_name_placeholder' })}
          error={formik.touched.last_name ? formik.errors.last_name : ''}
        />
      )}

      {get(questionnaireFields, 'cpf.visible') && (
        <CpfField
          required={get(questionnaireFields, 'cpf.required')}
          tooltip={f({ id: 'forms.signup.cpf_tooltip' })}
          boxProps={{ mt: 3 }}
          name='cpf'
          value={formik.values.cpf}
          onChange={formik.handleChange('cpf')}
          onBlur={formik.handleBlur('cpf')}
          label={f({ id: 'forms.signup.cpf_label' })}
          placeholder={f({ id: 'forms.signup.cpf_placeholder' })}
          error={formik.touched.cpf ? formik.errors.cpf : ''}
        />
      )}

      <PasswordField
        boxProps={{ mt: 3 }}
        name='password'
        type='password'
        value={formik.values.password}
        onChange={formik.handleChange('password')}
        onBlur={formik.handleBlur('password')}
        label={f({ id: 'forms.signup.password_label' })}
        placeholder='******'
        error={formik.touched.password ? formik.errors.password : ''}
        helperText={f({ id: 'forms.signup.password_helper' })}
      />

      <PasswordField
        boxProps={{ mt: 3 }}
        name='password_confirmation'
        type='password'
        value={formik.values.password_confirmation}
        onChange={formik.handleChange('password_confirmation')}
        onBlur={formik.handleBlur('password_confirmation')}
        label={f({ id: 'forms.signup.password_confirmation_label' })}
        placeholder='******'
        error={formik.touched.password_confirmation ? formik.errors.password_confirmation : ''}
      />

      <TermsOfUse
        value={formik.values.legal_agreement}
        onChange={() => formik.setFieldValue('legal_agreement', !formik.values.legal_agreement)}
        boxProps={{ mt: 4 }}
      />

      <Recaptcha setIsAllowed={(isAllowed) => { formik.setFieldValue('not_a_robot', isAllowed); }} />

      <Box
        mt='40px'
        display='flex'
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent={isMobile ? 'flex-start' : 'flex-end'}
      >
        <Button
          width={isMobile ? '75%' : ''}
          centerText={isMobile}
          type='submit'
          disabled={disabledButton}
        >
          {f({ id: 'forms.signup.signup_button' })}
        </Button>
      </Box>
    </form>
  );
};

SignUpFromEmailForm.propTypes = {
  location: PropTypes.object.isRequired,
};

export default SignUpFromEmailForm;
